import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  User, Phone, Mail, LogOut, Save, Loader2,
  CheckCircle2, AlertTriangle, ChevronLeft, Wallet,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import useAuthStore from '@/stores/authStore'
import { ROUTES } from '@/config/routes'
import Button from '@/components/ui/Button'

const BANK_OPTS = [
  { val: 'gcash', label: 'GCash'  },
  { val: 'maya',  label: 'Maya'   },
  { val: 'bank',  label: 'Bank Transfer' },
]

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[10px] text-ink-muted uppercase tracking-widest font-semibold mb-1.5">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </label>
      {children}
    </div>
  )
}

function Input({ value, onChange, placeholder, type = 'text', disabled }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-siglat-teal disabled:opacity-50 disabled:cursor-not-allowed"
    />
  )
}

export default function UserProfile() {
  const navigate   = useNavigate()
  const profile    = useAuthStore((s) => s.profile)
  const updateProf = useAuthStore((s) => s.updateProfile)
  const signOut    = useAuthStore((s) => s.signOut)

  const isWorker = profile?.role === 'worker'

  // Profile fields
  const [fullName, setFullName] = useState('')
  const [phone,    setPhone]    = useState('')

  // Worker-only payout fields
  const [bankType,    setBankType]    = useState('gcash')
  const [bankAccount, setBankAccount] = useState('')

  const [saving,   setSaving]   = useState(false)
  const [success,  setSuccess]  = useState(false)
  const [err,      setErr]      = useState(null)

  useEffect(() => {
    if (!profile) return
    setFullName(profile.full_name ?? '')
    setPhone(profile.phone ?? '')
  }, [profile?.id]) // eslint-disable-line

  useEffect(() => {
    if (!isWorker || !profile?.id) return
    supabase
      .from('worker_profiles')
      .select('bank_type, bank_account')
      .eq('id', profile.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return
        setBankType(data.bank_type ?? 'gcash')
        setBankAccount(data.bank_account ?? '')
      })
  }, [isWorker, profile?.id])

  async function handleSave() {
    if (!fullName.trim()) { setErr('Full name is required.'); return }
    setErr(null)
    setSaving(true)

    const { error: profErr } = await updateProf({
      full_name: fullName.trim(),
      phone:     phone.trim() || null,
    })

    if (profErr) { setErr('Failed to save: ' + profErr); setSaving(false); return }

    if (isWorker) {
      const { error: wpErr } = await supabase
        .from('worker_profiles')
        .update({ bank_type: bankType, bank_account: bankAccount.trim() || null })
        .eq('id', profile.id)
      if (wpErr) { setErr('Profile saved but payout details failed: ' + wpErr.message); setSaving(false); return }
    }

    setSuccess(true)
    setSaving(false)
    setTimeout(() => setSuccess(false), 3000)
  }

  async function handleSignOut() {
    await signOut()
    navigate(ROUTES.LOGIN, { replace: true })
  }

  const backTo = isWorker ? ROUTES.WORKER_HOME : ROUTES.BOOK_HISTORY

  return (
    <div className="min-h-dvh bg-surface-base">

      {/* Header */}
      <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-5 safe-top">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate(backTo)}
            className="p-1.5 text-ink-muted hover:text-ink-primary transition-colors rounded-lg hover:bg-surface-overlay"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-display text-xl font-bold text-ink-primary">My Profile</h1>
            <p className="text-xs text-ink-muted capitalize">{profile?.role ?? 'Account'} · {profile?.email}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-6 flex flex-col gap-5 pb-28">

        {/* Avatar / name block */}
        <div className="flex items-center gap-4 bg-surface-raised border border-surface-border rounded-xl p-4">
          <div className="w-14 h-14 bg-siglat-teal/15 rounded-2xl flex items-center justify-center shrink-0">
            <span className="text-siglat-teal text-xl font-black">
              {(profile?.full_name ?? 'U').charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold text-ink-primary truncate">{profile?.full_name ?? '—'}</p>
            <p className="text-xs text-ink-muted truncate">{profile?.email}</p>
            <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full border font-medium capitalize
              ${profile?.role === 'worker'
                ? 'text-blue-400 bg-blue-500/10 border-blue-500/25'
                : 'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25'}`}>
              {profile?.role ?? 'user'}
            </span>
          </div>
        </div>

        {/* Edit fields */}
        <div className="bg-surface-raised border border-surface-border rounded-xl p-4 flex flex-col gap-4">
          <p className="text-xs font-semibold text-ink-secondary uppercase tracking-widest">Account Details</p>

          <Field label="Full Name" icon={User}>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Juan Dela Cruz"
            />
          </Field>

          <Field label="Email" icon={Mail}>
            <Input value={profile?.email ?? ''} disabled />
            <p className="text-[10px] text-ink-muted mt-1">Email is managed through your login method and cannot be changed here.</p>
          </Field>

          <Field label="Mobile Number" icon={Phone}>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 09171234567"
              type="tel"
            />
            <p className="text-[10px] text-ink-muted mt-1">Used for SMS notifications about your bookings.</p>
          </Field>
        </div>

        {/* Worker: payout details */}
        {isWorker && (
          <div className="bg-surface-raised border border-surface-border rounded-xl p-4 flex flex-col gap-4">
            <p className="text-xs font-semibold text-ink-secondary uppercase tracking-widest flex items-center gap-1.5">
              <Wallet className="w-3.5 h-3.5" />
              Payout Details
            </p>
            <p className="text-xs text-ink-muted -mt-2">How you receive your job payout from Siglat Konstract.</p>

            <Field label="Payout Method">
              <div className="grid grid-cols-3 gap-2">
                {BANK_OPTS.map((opt) => (
                  <button
                    key={opt.val}
                    type="button"
                    onClick={() => setBankType(opt.val)}
                    className={`py-2 rounded-lg border text-xs font-medium transition-colors
                      ${bankType === opt.val
                        ? 'bg-siglat-teal/10 border-siglat-teal/40 text-siglat-teal'
                        : 'bg-surface-overlay border-surface-border text-ink-secondary hover:border-siglat-teal/30'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </Field>

            <Field label={bankType === 'bank' ? 'Account Number' : `${BANK_OPTS.find(o => o.val === bankType)?.label} Number`}>
              <Input
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value)}
                placeholder={bankType === 'bank' ? 'e.g. 001234567890' : 'e.g. 09171234567'}
                type="text"
              />
            </Field>
          </div>
        )}

        {/* Error */}
        {err && (
          <div className="flex items-center gap-2 p-3 bg-siglat-red/10 border border-siglat-red/25 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-siglat-red shrink-0" />
            <p className="text-sm text-siglat-red">{err}</p>
          </div>
        )}

        {/* Success */}
        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/25 rounded-lg">
            <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
            <p className="text-sm text-green-400">Profile saved successfully.</p>
          </div>
        )}

        {/* Save */}
        <Button onClick={handleSave} disabled={saving} fullWidth>
          {saving
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
            : <><Save className="w-4 h-4" /> Save Changes</>}
        </Button>

        {/* Sign out */}
        <button
          onClick={handleSignOut}
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-siglat-red/25 text-siglat-red text-sm font-medium hover:bg-siglat-red/8 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>

      </div>
    </div>
  )
}
